/**
 * QUIPWA — AppShell
 *
 * Frame of every main screen: sidebar (desktop), top and bottom bars (phone),
 * the sync status and attention banners, and the routed page.
 *
 * Information architecture (docs/modulos/ajustes-y-apariencia.md):
 *   Inicio · Mazos · Notas · Examen · Etiquetas · Ajustes
 * Appearance, account, sync and data all live inside Ajustes.
 *
 * Phones have no sidebar, so what it carries besides the destinations moves
 * to a top bar on the six top-level screens: global search (Ctrl/⌘+K has no
 * key to press on a phone), sync status, light mode and the account. Detail
 * screens (a note, a deck, the editor…) keep their own header instead.
 *
 * The Google session never gates this shell: only a device that has never
 * been signed in and never chose "start without an account" is sent to
 * onboarding (local-first: the app works fully without Google).
 */
import { Suspense, lazy, useEffect, useState } from 'react'
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { FileText, Home, Layers, MessageCircleQuestion, Plus, Search, Settings, Tag } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useHistoryOverlay } from '../../hooks/useHistoryOverlay'
import { isOnboardingCompleted } from '../../lib/auth/local-storage'
import { useI18n } from '../../lib/i18n/i18n-store'
import { isChunkLoadError, reloadForNewBuild } from '../../lib/pwa/chunk-reload'
import { SyncConflictBanner } from './SyncConflictBanner'
import { SyncAttentionBanner } from '../system/SyncAttentionBanner'
import { SyncStatusChip } from '../system/SyncStatusChip'
import { Avatar } from '../system/Avatar'
import { LightModeToggle } from '../system/LightModeToggle'
import { QuipuMark } from '../system/QuipuMark'
import { RouteFallback } from '../../routes/RouteFallback'
import { ErrorBoundary } from './ErrorBoundary'
import '../../styles/AppShell.css'

// Global search (Ctrl/⌘+K) and its index load on demand, outside the first screen.
// Same chunk-reload handling as lazyPage (routes/lazy-page.ts), which only takes prop-less pages.
const loadCommandPalette = () => import('./CommandPalette')
const CommandPalette = lazy(async () => {
  try {
    return { default: (await loadCommandPalette()).CommandPalette }
  } catch (err) {
    if (isChunkLoadError(err) && reloadForNewBuild()) return new Promise<never>(() => {})
    throw err
  }
})

const NAV = [
  { to: '/', end: true, icon: Home, key: 'nav.home' },
  { to: '/decks', icon: Layers, key: 'nav.decks' },
  { to: '/notes', icon: FileText, key: 'nav.notes' },
  { to: '/socratic', icon: MessageCircleQuestion, key: 'nav.exam' },
  { to: '/tags', icon: Tag, key: 'nav.tags' },
] as const

/** The screens that show the phone top bar: the bottom bar's destinations. */
const TOP_LEVEL = new Set<string>([...NAV.map((item) => item.to), '/settings'])

function navClass(base: string) {
  return ({ isActive }: { isActive: boolean }) => `${base}${isActive ? ' is-active' : ''}`
}

function SidebarAccount() {
  const { user } = useAuth()
  const { t } = useI18n()
  if (!user) return null
  return (
    <NavLink to="/settings#account" className="sidebar-user" aria-label={t('settings.account.title')}>
      <Avatar name={user.name} picture={user.picture} />
      <span className="sidebar-user-info">
        <span className="sidebar-user-name">{user.name.split(' ')[0]}</span>
        <span className="sidebar-user-email">{user.email}</span>
      </span>
    </NavLink>
  )
}

/** The phone top bar's account button: the avatar, to Ajustes → Cuenta. */
function TopBarAccount() {
  const { user } = useAuth()
  const { t } = useI18n()
  if (!user) return null
  return (
    <NavLink to="/settings#account" className="btn btn--ghost btn--icon app-topbar-btn" aria-label={t('settings.account.title')}>
      <Avatar name={user.name} picture={user.picture} size={28} />
    </NavLink>
  )
}

export function AppShell() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { status, hasAccount } = useAuth()
  const { t } = useI18n()
  // The palette has its own history entry on the screen it was opened on:
  // the phone's back gesture closes it and stays (hooks/useHistoryOverlay.ts).
  const palette = useHistoryOverlay('palette')
  const { show: openPalette } = palette
  // Stays mounted after the first opening (closed, it renders nothing).
  const [paletteMounted, setPaletteMounted] = useState(false)
  if (palette.open && !paletteMounted) setPaletteMounted(true)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Not with Alt: on Windows, AltGr arrives as Ctrl+Alt.
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        openPalette()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [openPalette])

  // Fetch the palette's code once start-up is over, so the first Ctrl+K opens at once.
  useEffect(() => {
    const preload = () => void loadCommandPalette().catch(() => {})
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(preload, { timeout: 5000 })
      return () => cancelIdleCallback(id)
    }
    const timer = setTimeout(preload, 3000)
    return () => clearTimeout(timer)
  }, [])

  if (status === 'signed_out' && !hasAccount && !isOnboardingCompleted()) return <Navigate to="/onboarding" replace />

  return (
    <div className="app-shell">
      <nav className="sidebar" aria-label={t('nav.main')}>
        <div className="sidebar-brand">
          <button className="sidebar-logo" onClick={() => navigate('/')} aria-label={t('nav.home')}>
            <QuipuMark size={24} />
            <span className="sidebar-logo-text">QUIPWA</span>
          </button>
          <LightModeToggle />
        </div>

        <div className="sidebar-nav">
          {NAV.map(({ to, icon: Icon, key, ...rest }) => (
            <NavLink key={to} to={to} end={'end' in rest} className={navClass('sidebar-item')}>
              <Icon size={18} aria-hidden="true" />
              <span>{t(key)}</span>
            </NavLink>
          ))}
          <div className="sidebar-divider" role="separator" />
          <NavLink to="/settings" className={navClass('sidebar-item')}>
            <Settings size={18} aria-hidden="true" />
            <span>{t('nav.settings')}</span>
          </NavLink>
        </div>

        <div className="sidebar-footer">
          <NavLink to="/import" className="btn btn--primary sidebar-import-btn">
            <Plus size={16} aria-hidden="true" />
            <span>{t('nav.import')}</span>
          </NavLink>
          <SyncStatusChip />
          <SidebarAccount />
        </div>
      </nav>

      <main className="app-main" id="main">
        {/* Phones only (CSS): the sidebar's brand row and footer, condensed. */}
        {TOP_LEVEL.has(pathname) && (
          <header className="app-topbar">
            <span className="app-topbar-brand" aria-hidden="true">
              <QuipuMark size={22} />
              <span className="app-topbar-name">QUIPWA</span>
            </span>
            <div className="app-topbar-actions">
              <button
                type="button"
                className="btn btn--ghost btn--icon app-topbar-btn app-topbar-search"
                aria-label={t('nav.search')}
                aria-haspopup="dialog"
                onClick={openPalette}
              >
                <Search size={19} aria-hidden="true" />
              </button>
              <SyncStatusChip compact />
              <LightModeToggle className="btn btn--ghost btn--icon app-topbar-btn" />
              <TopBarAccount />
            </div>
          </header>
        )}
        <SyncAttentionBanner />
        <SyncConflictBanner />
        {/* Screens load on demand (src/routes/pages.ts): the sidebar stays put while one
            arrives, and stays usable if one fails. The key resets the boundary on navigation. */}
        <ErrorBoundary key={pathname} level="section">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>

      {paletteMounted && (
        // A palette that fails to load must not take the shell down with it.
        <ErrorBoundary level="component" fallback={() => null}>
          <Suspense fallback={null}>
            <CommandPalette isOpen={palette.open} onClose={palette.close} onNavigate={palette.go} />
          </Suspense>
        </ErrorBoundary>
      )}

      {/* Each key carries a LED that only a skin shows (lit on the current screen). */}
      <nav className="bottom-nav" aria-label={t('nav.main')}>
        {NAV.map(({ to, icon: Icon, key, ...rest }) => (
          <NavLink key={to} to={to} end={'end' in rest} className={navClass('bottom-nav-item')}>
            <span className="led" aria-hidden="true" />
            <Icon size={20} aria-hidden="true" />
            <span>{t(key)}</span>
          </NavLink>
        ))}
        <NavLink to="/settings" className={navClass('bottom-nav-item')}>
          <span className="led" aria-hidden="true" />
          <Settings size={20} aria-hidden="true" />
          <span>{t('nav.settings')}</span>
        </NavLink>
      </nav>
    </div>
  )
}
